import { Call, Sms } from '@mui/icons-material';
import { Box, Link, Typography } from '@mui/material';
import React from 'react';

/**
 * Reusable support contact card for the SOS crisis page.
 *
 * Displays an organisation name, availability, and one or more
 * tel/sms action links with WCAG 2.2 AA compliant touch targets.
 */

// -- Colour tokens (must match SOSPage) --------------------------------------
const SOS_BLUE = '#0B4FCC';
const TEXT_PRIMARY = '#111111';
const TEXT_MUTED = '#555555';

const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const FOCUS_VISIBLE_SX = {
  '&:focus-visible': {
    outline: '3px solid #000000',
    outlineOffset: '3px',
  },
} as const;

const CONTACT_LINK_SX = {
  fontFamily: FONT_FAMILY,
  fontWeight: 600,
  fontSize: 15,
  color: SOS_BLUE,
  textDecorationColor: SOS_BLUE,
  display: 'inline-block',
  minHeight: 48,
  lineHeight: '48px',
  ...FOCUS_VISIBLE_SX,
} as const;

// -- Types -------------------------------------------------------------------

export interface ContactAction {
  label: string;
  href: string;
  fallbackHref?: string;
  icon: 'call' | 'sms';
}

export interface SupportContactProps {
  name: string;
  availability: string;
  actions: ContactAction[];
}

// -- Component ---------------------------------------------------------------

export default function SupportContact({
  name,
  availability,
  actions,
}: SupportContactProps): React.ReactElement {
  return (
    <Box
      sx={{
        bgcolor: '#F8F8F8',
        borderRadius: '12px',
        px: 2,
        py: 1.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT_FAMILY,
          fontWeight: 700,
          fontSize: 15,
          color: TEXT_PRIMARY,
        }}
      >
        {name}
        {availability && (
          <Typography
            component="span"
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 400,
              fontSize: 13,
              color: TEXT_MUTED,
              ml: 1,
            }}
          >
            ({availability})
          </Typography>
        )}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          // Reason: minimum 8px (1.5 = 12px) spacing between buttons
          gap: 1.5,
          mt: 1,
        }}
      >
        {actions.map((action) => (
          <Link
            key={action.label}
            href={action.href}
            aria-label={`${action.label} — ${name}`}
            sx={{
              ...CONTACT_LINK_SX,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: '#FFFFFF',
              border: '1px solid #D8DADC',
              borderRadius: '8px',
              px: 2,
              py: 1,
              textDecoration: 'none',
              // Reason: WCAG 2.2 Target Size (Minimum) — 48px
              minHeight: 48,
              lineHeight: 1.4,
              '&:hover': {
                bgcolor: '#F0F0F0',
                textDecoration: 'none',
              },
            }}
          >
            {action.icon === 'call' ? (
              <Call sx={{ fontSize: 18, color: SOS_BLUE }} />
            ) : (
              <Sms sx={{ fontSize: 18, color: SOS_BLUE }} />
            )}
            {action.label}
          </Link>
        ))}
      </Box>
    </Box>
  );
}
